import React, { useState } from 'react';
import { Invitation, SiteRegistrationForm } from '../../types';
import FormFieldSet from '../FormFieldSet';
import FloatingLabel from '../FloatingLabel';
import { getCookie } from '../../../../utils';
import { getManufacturerPortalName, doesManufacturerRequireManualAuth } from '../../utils';

import { InformationCircleIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ExternalAuthorisation } from '../../../../types';

interface AuthStepProps {
    formData: SiteRegistrationForm;
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    display?: boolean;
    authorised: boolean;
    setAuthorised: (authorised: boolean) => void;
    externalAuthorisations: ExternalAuthorisation[];
    selectedExternalAuthorisation: ExternalAuthorisation | null;
    setSelectedExternalAuthorisation: (authorisation: ExternalAuthorisation | null) => void;
    newExternalAuthorisation: ExternalAuthorisation | null;
    setNewExternalAuthorisation: (authorisation: ExternalAuthorisation | null) => void;
    invitation?: Invitation | null;
}

interface AuthFormProps {
    fieldKey: string;
    formData: SiteRegistrationForm;
    handleInputChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    display?: boolean;
}

const AuthForm = ({ fieldKey, formData, handleInputChange, display = false }: AuthFormProps) => {
    let placeholder, inputType;
    if (fieldKey === 'external_access_token') {
        placeholder = 'Access Token';
        inputType = 'password';
    }
    if (display) {
        return (
            <div className={'bg-base-100 border rounded'}>
                <div>{placeholder}: {formData[fieldKey as keyof SiteRegistrationForm] ? formData[fieldKey as keyof SiteRegistrationForm] : '-'}</div>
            </div>
        );
    } else {
        return (
            <input
                type={inputType}
                id={fieldKey}
                name={fieldKey}
                value={formData[fieldKey as keyof SiteRegistrationForm] ?? ''}
                onChange={handleInputChange}
                className="input focus:outline-none"
                placeholder={placeholder}
            />
        );
    }
}

const AuthStep: React.FC<AuthStepProps> = ({ formData, handleInputChange, display = false, authorised, setAuthorised, externalAuthorisations, selectedExternalAuthorisation, setSelectedExternalAuthorisation, newExternalAuthorisation, setNewExternalAuthorisation, invitation = null }) => {
    const [isAuthorising, setIsAuthorising] = useState(false);
    const [showExternalAuthorisations, setShowExternalAuthorisations] = useState(formData.manufacturer && externalAuthorisations.length > 0);

    const handleUseExternalAuthorisation = (authorisation: ExternalAuthorisation) => (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setSelectedExternalAuthorisation(authorisation);
        setAuthorised(true);
    }

    const handleAuthorise = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!formData.external_access_token) {
            return;
        }
        setIsAuthorising(true);
        
        fetch(invitation ? `/api/proxy_external_api_public/${invitation.token}/` : '/api/proxy_external_api/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken') || ''
            },
            body: JSON.stringify({
                url: '/users/me',
                manufacturer: formData.manufacturer,
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Authorization': `Token ${formData.external_access_token}`
                },
                body: {
                    'access_token': formData.external_access_token
                }
            })
        })
            .then(response => response.json())
            .then(data => {
                if (data.status === 'OK') {
                    setAuthorised(true);
                    setNewExternalAuthorisation(data.external_authorisation);
                    setSelectedExternalAuthorisation(data.external_authorisation);
                } else {
                    setAuthorised(false);
                }
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                setIsAuthorising(false);
            });
    }

    const authoriseButtonDisabled = !formData.external_access_token || isAuthorising || authorised;

    return (
        <FormFieldSet legend="Authorisation">
            { showExternalAuthorisations &&
                <div>
                    <div className="flex items-center gap-1 mb-4">
                        <InformationCircleIcon className="w-4 h-4" />
                        <p>You have already authorised with the following manufacturers:</p>
                    </div>
                    <ul className="list">
                        <li className="flex items-center gap-1">
                            <div className="flex-1 font-bold">Manufacturer</div>
                            <div className="flex-1 font-bold">Token ID</div>
                            <div className="flex-2 font-bold">External Account</div>
                            <div className="flex-1 font-bold">External ID</div>
                            <div className="flex-1 font-bold"></div>
                        </li>
                        { externalAuthorisations.map(authorisation => (
                                <li key={authorisation.manufacturer + authorisation.external_id} className={`flex items-center gap-1 rounded-box p-2 ${selectedExternalAuthorisation?.external_token_id === authorisation.external_token_id ? 'bg-base-200' : ''}`}>
                                    <div className="flex-1">{authorisation.manufacturer}</div>
                                    <div className="flex-1">{authorisation.external_token_id}</div>
                                    <div className="flex-2">{authorisation.external_account}</div>
                                    <div className="flex-1">{authorisation.external_id}</div>
                                    <button
                                        className="flex-1 btn btn-link btn-sm p-0 text-base-content hover:text-neutral no-underline"
                                        style={{ visibility: selectedExternalAuthorisation?.external_token_id === authorisation.external_token_id ? 'hidden' : 'visible' }}
                                        onClick={handleUseExternalAuthorisation(authorisation)}>Use this</button>
                                </li>
                            )) }
                    </ul>
                </div>
            }
            { !formData.manufacturer &&
                <div className="flex items-center gap-1">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    Please select a manufacturer to continue.
                </div>
            }
            { !showExternalAuthorisations && !authorised && <div className="space-y-3">
                { !authorised && <div className="flex items-center gap-1">
                        <InformationCircleIcon className="w-4 h-4" />
                        <p>Enter your {getManufacturerPortalName(formData.manufacturer)} access token to authorise with {formData.manufacturer}.</p>
                    </div>
                }
                { doesManufacturerRequireManualAuth(formData.manufacturer) && !authorised && (
                    <div className="flex items-center gap-1">
                        <FloatingLabel className="w-1/2">
                            <span>Access Token</span>
                            <AuthForm fieldKey="external_access_token" formData={formData} handleInputChange={handleInputChange} display={display} />
                        </FloatingLabel>
                    </div>
                ) }
                <div className="flex items-center gap-2 mt-2">
                    { doesManufacturerRequireManualAuth(formData.manufacturer) && (
                        <button className='btn btn-neutral' onClick={handleAuthorise} disabled={authoriseButtonDisabled}>
                            {isAuthorising ? (
                                    <div className="flex items-center">
                                        <span className="loading loading-dots loading-sm mr-2"></span>
                                        Authorising
                                    </div>
                                ) : ( authorised ? <div className="flex items-center gap-1">
                                    <CheckCircleIcon className="w-4 h-4" />
                                    Authorised
                                </div> : 'Authorise' )
                            }
                        </button>
                    ) }
                    { externalAuthorisations.length > 0 && <button className='btn btn-link btn-sm btn-base-300 p-0 text-base-content hover:text-neutral no-underline' onClick={() => {
                        setShowExternalAuthorisations(true);
                        setSelectedExternalAuthorisation(null);
                        setAuthorised(false);
                    }}>
                        Use existing authorisations
                    </button> }
                </div>
            </div> }
            { showExternalAuthorisations &&<div className="mt-2 flex justify-center">
                { formData.manufacturer && showExternalAuthorisations && (
                    <button className='btn btn-link btn-sm btn-base-300 p-0 text-base-content hover:text-neutral no-underline' onClick={() => {
                        setShowExternalAuthorisations(false);
                        setSelectedExternalAuthorisation(null);
                        setAuthorised(false);
                    }}>
                        Register new authorisation
                    </button>
                ) }
            </div> }
            { authorised && newExternalAuthorisation && <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-1">
                    <InformationCircleIcon className="w-4 h-4" />
                    You have successfully authorised with {getManufacturerPortalName(formData.manufacturer)}.
                </div>
                <div>
                    <ul className="list">
                        <li>External ID: {newExternalAuthorisation.external_id}</li>
                        <li>External Account: {newExternalAuthorisation.external_account}</li>
                        <li>External Manufacturer: {newExternalAuthorisation.manufacturer}</li>
                    </ul>
                </div>
            </div> }
        </FormFieldSet>
    );
};

export default AuthStep;